import type { BlockModelUpdatePayload } from '@aytm/app/insights/dashboard/models/BlockModel'
import type { ServerResponse } from '@aytm/app/insights/dashboard/models/types.ServerResponse'
import type { ComponentStoreState } from '@aytm/app/insights/dashboard/stores/ComponentStoreState'

import { toKeyedRecord } from '@aytm/app/insights/dashboard/lib/arrays'
import { convertFilterOptions } from '@aytm/app/insights/dashboard/lib/filters'

import type { Identity } from '@aytm/app/insights/dashboard/helperTypes'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import { AttributeType, SubjectType, DataItem, StatItem } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { updateCurrentData } from '@aytm/app/insights/dashboard/helpers/requests'
import { sanitizeSources, sanitizeFilters } from '@aytm/app/insights/dashboard/helpers/filters'
import { convertSchema } from '@aytm/app/insights/dashboard/helpers/schema'

export type ProcessedResult = {
  blockUID: string
  isAlternativeBlockUID: boolean
  sources: unknown
  sourcesChanged: boolean
  dataUpdatePayload: Partial<ComponentStoreState>
  schemaUpdatePayload: Partial<BlockModelUpdatePayload>
}

export type PositiveServerResponse = Identity<Extract<ServerResponse.UnifiedServerResponseType, { status: 'OK' }>>

export type FailedServerResponse = Identity<Extract<ServerResponse.UnifiedServerResponseType, { status: 'FAIL' }>>

export type ProcessResultHandler = (
  response: PositiveServerResponse,
  state: InsightsDashboardPoolState
) => InsightsDashboardPoolState

const handleAttributesInResponse: ProcessResultHandler = (response, state) => {
  if ('attributes' in response && response.attributes) {
    const attributes = toKeyedRecord<ServerResponse.InsightsSchema.AttributeType, string, AttributeType>(
      response.attributes,
      'key'
    )
    return updateCurrentData(state, { attributes })
  } else {
    return state
  }
}

const handleWavesInResponse: ProcessResultHandler = (response, state) => {
  if ('waves' in response && response.waves) {
    const waves = response.waves
    return updateCurrentData(state, { waves })
  } else {
    return state
  }
}

const handleSplitsInResponse: ProcessResultHandler = (response, state) => {
  if ('splits' in response && response.splits) {
    const splits = response.splits
    return updateCurrentData(state, { splits })
  } else {
    return state
  }
}

const handlePresetsInResponse: ProcessResultHandler = (response, state) => {
  if ('presets' in response && response.presets) {
    const newPresets = response.presets.reduce(
      (accumulatedPresets, cur) =>
        Object.assign(accumulatedPresets, {
          [cur.label]: Object.entries(cur.attributes).reduce(
            (memo, entry) => Object.assign(memo, { [entry[0]]: entry[1].uids }),
            ('All' === cur.label ? { _: [] } : {}) as Record<string, string[]>
          )
        }),
      {} as Record<string, Record<string, string[]>>
    )
    return updateCurrentData(state, { presets: newPresets })
  } else {
    return state
  }
}

const handleSubjectsInResponse: ProcessResultHandler = (response, state) => {
  let resultState = state
  if ('subjects' in response && response.subjects) {
    const subjects = toKeyedRecord<ServerResponse.InsightsSchema.SubjectType, string, SubjectType>(
      response.subjects,
      'uid'
    )
    resultState = updateCurrentData(resultState, { subjects })
  }
  return resultState
}

const handleSummaryInResponse: ProcessResultHandler = (response, state) => {
  if ('summary' in response && response.summary) {
    const portalSurveys = response.summary.surveys
    const totalSubjects = response.summary.subjects
    const totalResponses = response.summary.responses
    const totalRespondents = response.summary.respondents
    return updateCurrentData(state, {
      portalSurveys,
      totalSubjects,
      totalResponses,
      totalRespondents
    })
  } else {
    return state
  }
}

const handleQueryHashInResponse: ProcessResultHandler = (response, state) => {
  if ('queryHash' in response && response.queryHash) {
    const queryHash = response.queryHash

    return updateCurrentData(state, { queryHash })
  } else {
    return state
  }
}

const handleFilterOrSourceOptionsInResponse: ProcessResultHandler = (response, state) => {
  let resultState = state
  if (
    ('filterOptions' in response && response.filterOptions) ||
    ('sourceOptions' in response && response.sourceOptions)
  ) {
    // Process source options
    const sourceOptions = response.sourceOptions ? convertFilterOptions(response.sourceOptions) : null
    if (sourceOptions) {
      resultState = updateCurrentData(resultState, { sourceOptions })
      resultState = sanitizeSources(resultState, sourceOptions)
    }
    // Process filter options
    const filterOptions = response.filterOptions ? convertFilterOptions(response.filterOptions) : null
    if (filterOptions) {
      resultState = updateCurrentData(resultState, { filterOptions })
      resultState = sanitizeFilters(resultState, filterOptions)
    }
  }
  return resultState
}

const handleConvertSchemaVersion: ProcessResultHandler = (_response, state) => convertSchema(state)

export const schemaHandlers = [
  handleAttributesInResponse,
  handleWavesInResponse,
  handleSplitsInResponse,
  handlePresetsInResponse,
  handleSubjectsInResponse,
  handleSummaryInResponse,
  handleQueryHashInResponse,
  handleFilterOrSourceOptionsInResponse,
  handleConvertSchemaVersion
]

const handleDataHashesInResponse: ProcessResultHandler = (response, state) => {
  if ('dataHashes' in response && response.dataHashes) {
    const selectedSubjects = response.dataHashes.subjects
    const selectedResponses = response.dataHashes.responses
    const selectedRespondents = response.dataHashes.respondents
    const dataFiltered = Boolean(response.dataHashes.filtered)
    const dataMetrics = response.dataHashes.metrics
    const data = response.dataHashes.items
    return updateCurrentData(state, {
      selectedSubjects,
      selectedResponses,
      selectedRespondents,
      dataFiltered,
      dataMetrics,
      data,
      dataFetched: true,
      dataUpdating: false
    })
  } else {
    return state
  }
}

export const dataHashesHandlers = [handleDataHashesInResponse]

const handleDataItemsInResponse: ProcessResultHandler = (response, state) => {
  if ('dataItems' in response && response.dataItems?.items) {
    // Requested hashes
    const hashes = response.hashes || []
    // Update store with new data items
    const data = Object.entries(state.currentData?.data || {}).reduce(
      (acc, [uid, val]) => {
        const hash = 'string' === typeof val ? val : val.hash || ''
        acc[uid] = response.dataItems.items[uid] || val
        if ('object' === typeof acc[uid]) {
          // Set value for fetched item
          acc[uid] = {
            ...acc[uid],
            hash
          }
        } else if ('string' === typeof acc[uid] && hashes.includes(acc[uid])) {
          // Set missing values to 'empty'
          acc[uid] = 'empty'
        }
        return acc
      },
      {} as Record<string, DataItem | string>
    )
    return updateCurrentData(state, { data })
  }

  return state
}

export const dataItemsHandlers = [handleDataItemsInResponse]

const handleStatHashesInResponse: ProcessResultHandler = (response, state) => {
  if ('statHashes' in response && response.statHashes) {
    const stat = response.statHashes.items
    return updateCurrentData(state, { stat })
  } else {
    return state
  }
}

export const statHashesHandlers = [handleStatHashesInResponse]

const handleStatItemsInResponse: ProcessResultHandler = (response, state) => {
  if ('statItems' in response && response.statItems?.items) {
    // Requested hashes
    const hashes = response.hashes || []
    // Update store with new stat items
    const stat = Object.entries(state.currentData?.stat || {}).reduce(
      (acc, [uid, val]) => {
        // Set data from response or keep existing value
        acc[uid] = response.statItems.items[uid] || val
        // Set missing values to 'empty'
        if ('string' === typeof acc[uid] && hashes.includes(acc[uid])) {
          acc[uid] = 'empty'
        }
        return acc
      },
      {} as Record<string, StatItem | string>
    )
    return updateCurrentData(state, { stat })
  } else {
    return state
  }
}

export const statItemsHandlers = [handleStatItemsInResponse]
